import { ApolloCache, ApolloClient } from '@apollo/client'
import { dataIdFromObject } from '@sketch/graphql-cache'

import { updateCacheEntries, UpdaterFunction } from './updateCacheEntries'
import { Obj } from './traverseAndCollectRefs'

type ScopeValidator = (key: string) => boolean

interface Identity {
  __typename: string
}

export function removeFromPagination(
  cache: ApolloCache<any>,
  pagination: Obj,
  toBeDeletedId: string,
  decrementTotalCount = true
): boolean {
  const { entries, meta }: any = pagination || {}
  if (!Array.isArray(entries)) return false

  const index = entries.findIndex(x => cache.identify(x) === toBeDeletedId)
  if (index === -1) return false

  entries.splice(index, 1)

  if (decrementTotalCount && meta && typeof meta.totalCount === 'number') {
    meta.totalCount--
  }
  return true
}

function isPaginationObj(
  obj: Obj
): obj is { entries: unknown[]; meta?: { totalCount: number } } {
  return (
    typeof obj === 'object' && 'entries' in obj && Array.isArray(obj.entries)
  )
}

export function removeFromPaginated<
  ToBeDeletedIdentity extends Identity,
  ParentIdentity extends Identity,
>(
  cache: ApolloCache<any> | ApolloClient<any>,
  toBeDelete: ToBeDeletedIdentity,
  scopeValidator: ParentIdentity | ScopeValidator,
  decrementTotalCount = true,
  metaUpdater: undefined | ((pagination: unknown) => void) = undefined
) {
  const toBeDeletedId = dataIdFromObject(toBeDelete)
  if (!toBeDeletedId) return

  const apolloCache = 'cache' in cache ? cache.cache : cache

  const updater: UpdaterFunction = object => {
    if (
      removeFromPagination(
        apolloCache,
        object,
        toBeDeletedId,
        decrementTotalCount
      )
    ) {
      // if an item was removed we can also call the metaUpdater
      metaUpdater?.(object?.meta)
    }
  }

  if (typeof scopeValidator === 'function') {
    return updateCacheEntries({
      cache: apolloCache,
      shouldTraverse: () => true,
      shouldCollect: ({ path, object }) =>
        isPaginationObj(object) && scopeValidator(path),
      updater,
    })
  }

  const parentCacheId = dataIdFromObject(scopeValidator)

  if (!parentCacheId) return

  return updateCacheEntries({
    cache: apolloCache,
    shouldTraverse: ({ path }) => path.includes(parentCacheId),
    shouldCollect: ({ object }) => isPaginationObj(object),
    updater,
  })
}
